import { ComponentType, LazyExoticComponent, PropsWithChildren, Suspense, lazy } from 'react';
import Loader from '@/components/ui/loader';

const CHUNK_RELOAD_KEY = 'jw:route-chunk-reload-at';
const CHUNK_RELOAD_COOLDOWN_MS = 30_000;

type ModuleWithDefault<T> = { default: T };

/**
 * `React.lazy` with a safe update path for code-split routes.
 *
 * After a deploy, a tab that is still running the previous build can navigate to a
 * route whose hashed chunk no longer exists on the origin. Instead of surfacing a
 * broken page we reload once: `index.html` is served with `Cache-Control: no-cache`,
 * so the reload picks up the current HTML and therefore the current chunk set.
 * A session-scoped cooldown prevents reload loops if the failure is persistent
 * (offline, blocked script, ...) — in that case the error is rethrown as before.
 *
 * Import paths passed to `factory` must stay literal so Vite can split them.
 */
export function lazyRoute<P>(
    factory: () => Promise<ModuleWithDefault<ComponentType<P>>>,
): LazyExoticComponent<ComponentType<P>> {
    type T = ComponentType<P>;
    return lazy(() =>
        factory().catch((error: unknown) => {
            const lastReloadAt = Number(sessionStorage.getItem(CHUNK_RELOAD_KEY) || 0);
            if (Date.now() - lastReloadAt > CHUNK_RELOAD_COOLDOWN_MS) {
                sessionStorage.setItem(CHUNK_RELOAD_KEY, String(Date.now()));
                window.location.reload();
                // Keep the Suspense fallback on screen while the reload happens.
                return new Promise<ModuleWithDefault<T>>(() => undefined);
            }
            throw error;
        }),
    );
}

/** Fallback shown inside the authenticated shell while a route chunk downloads. */
export const RouteFallback = () => (
    <div className="flex h-full w-full items-center justify-center p-8">
        <Loader size="lg" />
    </div>
);

/** Fallback for standalone routes rendered outside the app shell. */
export const PageFallback = () => (
    <div className="flex min-h-screen w-full items-center justify-center bg-neutral-100 dark:bg-jw-canvas">
        <Loader size="lg" />
    </div>
);

/** Wraps a standalone (non-shell) lazy route element in a Suspense boundary. */
export const StandalonePage = ({ children }: PropsWithChildren) => (
    <Suspense fallback={<PageFallback />}>{children}</Suspense>
);
